import { Box, CircularProgress, Divider, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, useLogEffect } from "@src/appV2/lib/analytics";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Suspense, useState } from "react";
import { useSearchParam } from "react-use";

import { BunnyLogo, ClipboardLogo } from "../../components/logos";
import { HeaderContainer } from "../../containers/app/styles";
import { ShiftBoardContent } from "./Content";
import { ShiftBoardHeader } from "./Header";
import { useGetShiftBoardWorkplace } from "./hooks/useShiftBoardWorkplace";
import { useTimeSlot } from "./hooks/useTimeSlot";
import { LogoContainer } from "./LogoContainer";
import { convertDateToDateTimeSlot } from "./util/convert";

interface ShiftBoardProps {
  token: string;
}

function ShiftBoard(props: ShiftBoardProps) {
  const { token } = props;

  const workplace = useGetShiftBoardWorkplace({ token });
  // eslint-disable-next-line react/hook-use-state
  const [initialDate] = useState(new Date());
  const { dateTimeSlot, incrementTimeSlot, decrementTimeSlot, canIncrement, canDecrement } =
    useTimeSlot({
      initialValue: convertDateToDateTimeSlot(initialDate, workplace.timeZone),
      onChange: (newDateTimeSlot) => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_BOARD_PAGE_VIEWED, {
          workplaceId: workplace.id,
          workplaceName: workplace.name,
          date: newDateTimeSlot.date.toISOString(),
          timeSlot: newDateTimeSlot.timeSlot,
        });
      },
    });

  useLogEffect(APP_V2_USER_EVENTS.SHIFT_BOARD_PAGE_VIEWED, {
    workplaceId: workplace.id,
    workplaceName: workplace.name,
    date: dateTimeSlot.date.toISOString(),
    timeSlot: dateTimeSlot.timeSlot,
  });

  const JUST_CLIPBOARD_LOGO_URL = `${process.env.REACT_APP_BASE_PATH ?? "/"}justClipboard.png`;

  const maxWidth = 768;
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <HeaderContainer>
        <LogoContainer>
          <ClipboardLogo height={60} width={140} url={JUST_CLIPBOARD_LOGO_URL} />
          <BunnyLogo height={63} width={60} />
        </LogoContainer>
      </HeaderContainer>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 64px)",
          backgroundColor: "#FAFAFA",
        }}
      >
        <ShiftBoardHeader
          workplace={workplace}
          dateTimeSlot={dateTimeSlot}
          incrementTimeSlot={incrementTimeSlot}
          decrementTimeSlot={decrementTimeSlot}
          canIncrement={canIncrement}
          canDecrement={canDecrement}
          maxWidth={maxWidth}
        />
        <Divider sx={{ margin: 0 }} />
        <QueryErrorResetBoundary>
          {() => (
            <Suspense
              fallback={
                <Stack justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Stack>
              }
            >
              <ShiftBoardContent
                workplace={workplace}
                token={token}
                dateTimeSlot={dateTimeSlot}
                maxWidth={maxWidth}
              />
            </Suspense>
          )}
        </QueryErrorResetBoundary>
      </Box>
    </Box>
  );
}

export function ShiftBoardPage() {
  const token = useSearchParam("token");
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <QueryErrorResetBoundary>
        {() => (
          <Suspense
            fallback={
              <Stack justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Stack>
            }
          >
            {token && <ShiftBoard token={token} />}
          </Suspense>
        )}
      </QueryErrorResetBoundary>
    </Box>
  );
}
